import React, { Suspense, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Provider } from '@gadgetinc/react';
import { Box, Flex, IconButton, Text, Spinner, HStack } from '@chakra-ui/react';
import { MoonIcon, SunIcon } from '@chakra-ui/icons';
import { api } from '../../api';
import MICUThemeProvider, { useMICUTheme } from '../../lib/micu/MICUThemeProvider';
import { startSyncEngine } from '../../lib/micu/syncEngine';
import { startAlarmManager } from '../../lib/micu/alarmManager';
import AlarmBanner from './AlarmBanner';

// Start engines once on module load
let enginesStarted = false;

const MICUHeader = () => {
  const { mode, toggleMode, isDark } = useMICUTheme();

  return (
    <Flex
      as="header"
      align="center"
      justify="space-between"
      px={4}
      py={2}
      bg={isDark ? 'micu.bgSecondary' : 'micu.header'}
      borderBottom="1px solid"
      borderColor="micu.border"
      position="sticky"
      top={0}
      zIndex={100}
    >
      <HStack spacing={2}>
        <Text fontSize="lg" fontWeight="bold" color="micu.teal" letterSpacing="tight">
          MICU Medic
        </Text>
      </HStack>
      <IconButton
        aria-label="Toggle day/night mode"
        icon={isDark ? <SunIcon /> : <MoonIcon />}
        onClick={toggleMode}
        variant="micuGhost"
        size="sm"
      />
    </Flex>
  );
};

const MICULayoutInner = () => {
  const { isDark } = useMICUTheme();

  useEffect(() => {
    if (!enginesStarted) {
      startSyncEngine();
      startAlarmManager();
      enginesStarted = true;

      // Inject PWA manifest link
      if (!document.querySelector('link[href="/micu-manifest.json"]')) {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = '/micu-manifest.json';
        document.head.appendChild(link);
      }

      // Register service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('/micu-sw.js').catch(() => {});
      }
    }
  }, []);

  return (
    <Box
      minH="100vh"
      bg={isDark ? 'micu.bg' : 'micu.bg'}
      color={isDark ? 'micu.text' : 'micu.text'}
    >
      <MICUHeader />
      <AlarmBanner />
      <Box as="main" maxW="600px" mx="auto" pb={20}>
        <Suspense fallback={
          <Flex justify="center" align="center" minH="200px">
            <Spinner color="micu.teal" size="lg" />
          </Flex>
        }>
          <Outlet />
        </Suspense>
      </Box>
    </Box>
  );
};

const MICULayout = () => {
  const navigate = useNavigate();

  return (
    <Provider api={api} navigate={navigate} auth={{ signInPath: "/sign-in" }}>
      <MICUThemeProvider>
        <MICULayoutInner />
      </MICUThemeProvider>
    </Provider>
  );
};

export default MICULayout;
